import { ToastContext } from "context/ToastContext";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useContext, useState } from "react";
import {
  useCreatePayerAutoSelectionRuleMutation,
  useDeletePayerAutoSelectionRuleMutation,
  useGetPayerAutoSelectionRulesQuery,
  useUpdatePayerAutoSelectionRuleMutation,
} from "store/queries/payerAutoSelection";
import styled from "styled-components";
import {
  IPayerAutoSelectionRule,
  IPayerAutoSelectionRuleSaveRequest,
  PayerAutoSelectionRuleKind,
} from "types/PayerAutoSelection/PayerAutoSelection";

import { PayerAutoSelectionRuleDialog } from "./PayerAutoSelectionRuleDialog";
import { PayerAutoSelectionTester } from "./PayerAutoSelectionTester";
import {
  IStage,
  MEDICARE_ADVANTAGE_NEVER,
  MEDICARE_ADVANTAGE_TERMS,
  STAGES,
  accountsInWords,
  optionsInWords,
  sourceInWords,
} from "./payerAutoSelectionWording";

const RuleCard = styled.div<{ $off?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.4fr) auto;
  gap: 1rem;
  align-items: start;
  padding: 0.875rem 1rem;
  border: 1px solid var(--surface-300, #dee2e6);
  border-radius: 0.5rem;
  background: #ffffff;
  opacity: ${(props) => (props.$off ? 0.6 : 1)};

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const StageNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: var(--primary-color, #4f46e5);
  color: #ffffff;
  font-weight: 600;
  flex-shrink: 0;
`;

function Heading({ children }: { children: string }) {
  return (
    <div className="text-xs font-semibold uppercase text-color-secondary mb-1">
      {children}
    </div>
  );
}

const chip = (text: string, className = "surface-200 text-color") => (
  <Tag key={text} value={text} className={className} />
);

/** The PUT body that re-saves a rule unchanged apart from `patch`. */
export const ruleToSaveRequest = (
  rule: IPayerAutoSelectionRule,
  patch: Partial<IPayerAutoSelectionRuleSaveRequest> = {}
): IPayerAutoSelectionRuleSaveRequest => ({
  kind: rule.kind,
  keywords: rule.keywords,
  matchPolicyTypeMedicaid: rule.matchPolicyTypeMedicaid,
  targetPayerId: rule.targetPayerId,
  accountIds: rule.accountIds,
  sourceScope: rule.sourceScope,
  sourcePayerIds: rule.sourcePayerIds,
  wholeSegment: rule.wholeSegment,
  skipOutOfState: rule.skipOutOfState,
  prefixMatch: rule.prefixMatch,
  enabled: rule.enabled,
  priority: rule.priority,
  ...patch,
});

/**
 * CLE-2845 — Eligibility › Payer Auto-Selection. After every eligibility check these rules decide
 * whether the estimate's payer should change to the one the response says the patient really has.
 * Four stages, top to bottom, first match wins; a switch only goes to a payer the facility has set
 * up, and staff see it on the estimate and can revert it.
 */
export function PayerAutoSelectionTab() {
  const toast = useContext(ToastContext);
  const {
    data: rules,
    isLoading,
    isError,
  } = useGetPayerAutoSelectionRulesQuery();
  const [createRule, { isLoading: creating }] =
    useCreatePayerAutoSelectionRuleMutation();
  const [updateRule, { isLoading: updating }] =
    useUpdatePayerAutoSelectionRuleMutation();
  const [deleteRule] = useDeletePayerAutoSelectionRuleMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IPayerAutoSelectionRule | null>(null);

  const allRules = rules ?? [];

  const nextPriorityFor = (kind: PayerAutoSelectionRuleKind) =>
    allRules
      .filter((rule) => rule.kind === kind)
      .reduce((max, rule) => Math.max(max, rule.priority), 0) + 10;

  const fail = (summary: string, error: any, fallback: string) =>
    toast?.current?.show({
      severity: "error",
      summary,
      detail: error?.data?.message || error?.message || fallback,
    });

  const handleSave = async (body: IPayerAutoSelectionRuleSaveRequest) => {
    try {
      if (editing) {
        await updateRule({ id: editing.id, body }).unwrap();
      } else {
        await createRule(body).unwrap();
      }
      setDialogOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Rule saved",
        detail: "The next eligibility check uses it.",
      });
    } catch (error: any) {
      fail("Not saved", error, "The rule could not be saved.");
    }
  };

  const toggle = async (rule: IPayerAutoSelectionRule, enabled: boolean) => {
    try {
      await updateRule({
        id: rule.id,
        body: ruleToSaveRequest(rule, { enabled }),
      }).unwrap();
    } catch (error: any) {
      fail("Not changed", error, "The rule could not be changed.");
    }
  };

  const remove = async (rule: IPayerAutoSelectionRule) => {
    // eslint-disable-next-line no-alert
    const sure = window.confirm(
      `Remove the rule that switches to "${
        rule.targetPayer?.name ?? `payer #${rule.targetPayerId}`
      }" (${accountsInWords(rule)})? The next eligibility check stops using it.`
    );
    if (!sure) return;
    try {
      await deleteRule(rule.id).unwrap();
    } catch (error: any) {
      fail("Not removed", error, "The rule could not be removed.");
    }
  };

  const ruleCard = (rule: IPayerAutoSelectionRule) => (
    <RuleCard
      key={rule.id}
      $off={!rule.enabled}
      data-testid={`payerAutoSelection.rule.${rule.id}`}
    >
      <div>
        {rule.kind === "OUT_OF_AREA_DEFAULT" ? (
          <>
            <Heading>When</Heading>
            <div className="flex flex-wrap gap-1">
              {chip("Out-of-state Blue member")}
              {chip("no product in the plan name")}
            </div>
          </>
        ) : (
          <>
            <Heading>When the plan name contains</Heading>
            <div className="flex flex-wrap gap-1">
              {rule.keywords.map((keyword) =>
                chip(keyword, "bg-indigo-50 text-indigo-800 font-mono")
              )}
              {rule.matchPolicyTypeMedicaid &&
                chip("or PolicyType says Medicaid")}
            </div>
          </>
        )}
      </div>
      <div>
        <Heading>Switch to</Heading>
        <span className="font-medium">
          {rule.targetPayer?.name ?? `Payer #${rule.targetPayerId}`}
        </span>
      </div>
      <div>
        <Heading>Applies to</Heading>
        <div className="flex flex-wrap gap-1">
          {chip(accountsInWords(rule))}
          {chip(sourceInWords(rule))}
          {optionsInWords(rule).map((option) =>
            chip(option, "bg-yellow-50 text-yellow-900")
          )}
        </div>
      </div>
      <div className="flex align-items-center gap-2">
        <InputSwitch
          checked={rule.enabled}
          onChange={(event) => toggle(rule, !!event.value)}
          aria-label="Rule enabled"
          data-testid={`payerAutoSelection.rule.${rule.id}.enabled`}
        />
        <Button
          type="button"
          label="Edit"
          className="p-button-outlined p-button-sm"
          data-testid={`payerAutoSelection.rule.${rule.id}.edit`}
          onClick={() => {
            setEditing(rule);
            setDialogOpen(true);
          }}
        />
        <Button
          type="button"
          label="Remove"
          className="p-button-text p-button-danger p-button-sm"
          aria-label="Remove rule"
          data-testid={`payerAutoSelection.rule.${rule.id}.remove`}
          onClick={() => remove(rule)}
        />
      </div>
    </RuleCard>
  );

  const builtInCard = (
    <RuleCard data-testid="payerAutoSelection.builtIn">
      <div>
        <Heading>When the plan name contains</Heading>
        <div className="flex flex-wrap gap-1">
          {MEDICARE_ADVANTAGE_TERMS.map((term) =>
            chip(term, "bg-indigo-50 text-indigo-800 font-mono")
          )}
          {MEDICARE_ADVANTAGE_NEVER.map((term) =>
            chip(`never: ${term}`, "bg-red-50 text-red-800 font-mono")
          )}
        </div>
      </div>
      <div>
        <Heading>Switch to</Heading>
        <span className="font-medium">
          The carrier&apos;s Medicare Advantage payer
        </span>
        <small className="block text-color-secondary">
          same parent, type Medicare Advantage
        </small>
      </div>
      <div>
        <Heading>Applies to</Heading>
        <div className="flex flex-wrap gap-1">
          {chip("All facilities")}
          {chip("from Commercial selections")}
        </div>
      </div>
      <div>{chip("Built in")}</div>
    </RuleCard>
  );

  const stageSection = (stage: IStage) => {
    const stageRules = stage.kind
      ? allRules.filter((rule) => rule.kind === stage.kind)
      : [];
    return (
      <section
        key={stage.number}
        className="flex flex-column gap-2"
        data-testid={`payerAutoSelection.stage.${stage.number}`}
      >
        <div className="flex align-items-start gap-2">
          <StageNumber>{stage.number}</StageNumber>
          <div>
            <h3 className="m-0">{stage.title}</h3>
            <p className="m-0 text-sm text-color-secondary">
              {stage.description}
            </p>
          </div>
        </div>
        {stage.kind === null && builtInCard}
        {stage.kind !== null && stageRules.map(ruleCard)}
        {stage.kind !== null && stageRules.length === 0 && (
          <span className="text-sm text-color-secondary">No rules yet.</span>
        )}
      </section>
    );
  };

  return (
    <div className="flex flex-column gap-3" data-testid="payerAutoSelectionTab">
      <Surface>
        <div className="flex align-items-start justify-content-between gap-3 mb-3">
          <span className="text-sm text-color-secondary">
            When an eligibility check comes back, these rules decide whether the
            estimate&apos;s payer should change to what the response says the
            patient really has. Rules run top to bottom, first match wins. A
            switch only happens to a payer the facility has set up, and staff
            see it on the estimate and can revert it.
          </span>
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add rule"
            className="p-button-sm flex-shrink-0"
            data-testid="payerAutoSelection.add"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          />
        </div>

        {isLoading && (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner
              style={{ width: "2rem", height: "2rem" }}
              data-testid="payerAutoSelection.loading"
            />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="The Payer Auto-Selection rules could not be loaded."
            data-testid="payerAutoSelection.error"
          />
        )}
        {!isLoading && !isError && (
          <div className="flex flex-column gap-4">
            {STAGES.map(stageSection)}
          </div>
        )}
      </Surface>

      <PayerAutoSelectionTester />

      <PayerAutoSelectionRuleDialog
        visible={dialogOpen}
        rule={editing}
        saving={creating || updating}
        nextPriorityFor={nextPriorityFor}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </div>
  );
}
